import {
  ExprCall,
  Access,
  Aggregate,
  DataRole,
  Expr,
  ExprForm,
  ExprState,
  Difference,
  PctDifference,
  PctOfTotal,
  DateTime,
} from '@mode-switch/express';
import { CompiledFormula } from './compiled-formula';
import { Switch } from './switch';

function createExprFormSource(
  field: CompiledFormula,
  aggFn: ExprCall.Name | undefined,
  make: (expr: Access.Parsed | Aggregate.Parsed) => string
): string {
  if (field.__expr == null || field.attrName == null) {
    throw new Error(`Field with formula "${field.formula}" is invalid`);
  }

  const isAggOrAccess = <R extends DataRole>(
    expr?: Expr.Parsed | Expr.Compiled<R>
  ): expr is Access<R> | Aggregate<R> => {
    switch (expr?.form) {
      case ExprForm.Access:
      case ExprForm.Aggregate: {
        return true;
      }
      default: {
        return false;
      }
    }
  };

  const toParsed = <R extends DataRole>(expr: Access<R> | Aggregate<R>): Access.Parsed | Aggregate.Parsed => {
    switch (expr.state) {
      case ExprState.Compiled: {
        switch (expr.form) {
          case ExprForm.Access: {
            return Access.Parsed.make(expr.root, expr.path);
          }
          case ExprForm.Aggregate: {
            return Aggregate.Parsed.make(expr.root, expr.call, expr.path);
          }
        }
      }
    }
    return expr;
  };

  // Known form aggregate | analytic fn
  // with an aggregation that is not in
  // a calculated field
  if (field.hasAggFunc) {
    const aggName = aggFn ?? <ExprCall.Name>field.aggFunc;
    const aggForm = aggregate(field, aggName);
    const aggExpr = Switch.parse(aggForm);
    if (isAggOrAccess(aggExpr)) {
      const parsed = toParsed(aggExpr);
      return make(parsed);
    }
  }

  // Known form analytic fn with an aggregation
  // that is within a calculated field
  if (Switch.Expressions.isAnalytic(field.__expr)) {
    if (isAggOrAccess(field.__expr.expr)) {
      const parsed = toParsed(field.__expr.expr);
      return make(parsed);
    }
  }

  // Requires aggregation
  if (field.isAggregateable) {
    const aggName = aggFn ?? field.defaultAggregate;
    const aggForm = aggregate(field, aggName);
    const aggExpr = Switch.parse(aggForm);
    if (isAggOrAccess(aggExpr)) {
      const parsed = toParsed(aggExpr);
      return make(parsed);
    }
  }

  // Is already aggregated
  if (field.isAggregateStage) {
    const expr = Switch.parse(field.formula);
    if (isAggOrAccess(expr)) {
      const parsed = toParsed(expr);
      return make(parsed);
    }
  }

  // Hmm.. no-op
  return field.formula;
}

export function diff(field: CompiledFormula, aggFunc?: ExprCall.Name, relTo?: ExprForm.RelativeTo): string {
  return createExprFormSource(field, aggFunc, (expr) =>
    Difference.source(expr, relTo ?? field.relativeTo ?? ExprForm.RelativeTo.Prev)
  );
}

export function pctDiff(field: CompiledFormula, aggFunc?: ExprCall.Name, relTo?: ExprForm.RelativeTo): string {
  return createExprFormSource(field, aggFunc, (expr) =>
    PctDifference.source(expr, relTo ?? field.relativeTo ?? ExprForm.RelativeTo.Prev)
  );
}

export function pctOfTotal(field: CompiledFormula, aggFunc?: ExprCall.Name, totalUsing?: ExprForm.TotalUsing): string {
  return createExprFormSource(field, aggFunc, (expr) =>
    PctOfTotal.source(expr, totalUsing ?? Switch.Expressions.getDefaultTotalUsing(expr))
  );
}

export function clearAnalytic(field: CompiledFormula): string {
  if (field.__expr == null) {
    throw new Error('Field is invalid');
  }
  const analyticExpr = field.__expr;
  if (Switch.Expressions.isAnalytic(analyticExpr)) {
    const subExpr = analyticExpr.expr;
    if (Switch.Expressions.isAggregate(subExpr)) {
      return aggregate(field, subExpr.call as ExprCall.Name);
    } else if (Switch.Expressions.isAggregateStage(subExpr) || Switch.Expressions.isAnalyticStage(subExpr)) {
      return access(field);
    }

    return `[${analyticExpr.expr.name}]`;
  } else {
    return field.formula; // just leave it
  }
}

export function relativeTo(field: CompiledFormula, relTo: ExprForm.RelativeTo) {
  if (!field.hasAnalyticFunc) {
    // TODO: New error types
    throw new Error('Cannot execute relativeTo operation on non-analytic fields');
  }

  if (field.__expr == null) {
    throw new Error('Field is invalid');
  }

  const analyticExpr = field.__expr;
  switch (analyticExpr.form) {
    case ExprForm.PctDifference:
      return PctDifference.source(analyticExpr.expr, relTo);
    case ExprForm.Difference:
      return Difference.source(analyticExpr.expr, relTo);
    default:
      throw new Error('Cannot set relative for: ' + field.formula);
  }
}

export function dateTrunc(
  field: CompiledFormula,
  unit: DateTime.Unit,
  weekStart?: DateTime.WeekStart,
  yearStart?: DateTime.YearStart
) {
  const args: string[] = [`'${unit}'`, access(field)];
  const startOfWeek = DateTime.weekStart(weekStart ?? field.weekStart);
  const startOfYear = DateTime.yearStart(yearStart ?? field.yearStart);

  if (startOfWeek) {
    args.push(`'${startOfWeek}'`);
  }

  if (startOfYear) {
    // Can't be undefined when specifying yearStart
    if (startOfWeek === undefined) {
      args.push(`'${field.defaultWeekStart}'`);
    }

    args.push(`'${startOfYear}'`);
  }

  return `DATETRUNC(${args.join(', ')})`;
}

export function datePart(
  field: CompiledFormula,
  unit: DateTime.Unit,
  weekStart?: DateTime.WeekStart,
  yearStart?: DateTime.YearStart
) {
  const args: string[] = [`'${unit}'`, access(field)];
  const startOfWeek = DateTime.weekStart(weekStart ?? field.weekStart);
  const startOfYear = DateTime.yearStart(yearStart ?? field.yearStart);

  if (startOfWeek) {
    args.push(`'${startOfWeek}'`);
  }

  if (startOfYear) {
    // Can't be undefined when specifying yearStart
    if (startOfWeek === undefined) {
      args.push(`'${field.defaultWeekStart}'`);
    }

    args.push(`'${startOfYear}'`);
  }

  return `DATEPART(${args.join(', ')})`;
}

export function aggregate(field: CompiledFormula, funcName: ExprCall.Name | undefined): string {
  return `${funcName}(${access(field)})`;
}

export function access(field: CompiledFormula | string): string {
  if (typeof field !== 'string') {
    return `[${field.attrName}]`;
  }

  return `[${field}]`;
}

export function bin(field: CompiledFormula | string, binSize: number): string {
  return `BIN(${access(field)}, ${binSize})`;
}

export function roundOffBinSize(binSize = 1): number {
  // If binSize is greater than or equal to 1, use 2 decimals
  if (Math.abs(binSize) >= 1) {
    return parseFloat(binSize.toFixed(2));
  }

  // binSize is less than 1 (given a floor of 0), use first 2 significant digits
  return parseFloat(binSize.toPrecision(2));
}
